import type {CSSProperties} from 'react';
import {wedgeExactRole, wedgeRole} from './wedgeRoles.js';

export type WedgeRailPadLook = 'railpad-ghost' | 'railpad-dictate' | 'railpad-dictate-on' | 'railpad-clear' | 'railpad-clear-on' | 'railpad-seed' | 'railpad-undo';

const RAIL_PAD_LOOKS = new Set<string>(['railpad-ghost', 'railpad-dictate', 'railpad-dictate-on', 'railpad-clear', 'railpad-clear-on', 'railpad-seed', 'railpad-undo']);
export const isWedgeRailPadLook = (look: string | undefined): look is WedgeRailPadLook => look !== undefined && RAIL_PAD_LOOKS.has(look);

/* Released controls of the rail notepad (the Notes pad of the Brief when the journal is off, prep-screens-2 RailNotepad). The four header tools
   share one ghost face: a 1px hairline, a 6px radius, 3px by 8px padding, 24px tall, 10.5px semibold type with 0.2px tracking and the quiet ink.
   `railpad-ghost` (Visit stamp and Copy) keeps the card border. `railpad-dictate` and `railpad-clear` take the separator border, and the `-on`
   looks turn ink and border red while the mic listens or the second tap of Clear is due. Dictate lays out its mic and label in a row with a 4px
   gap; Clear declares no display, as the source. `railpad-seed` is the starter chip: a pill on the accent tint with accent ink, 5px by 10px
   padding, 26px tall, 11px semibold type that clips with an ellipsis at the width of its row. `railpad-undo` is the bare accent UNDO text of the
   cleared-note line. All sit beside other controls in a row of 6px to 8px gaps, so none draws a transparent target (wedgeRailPadDrawsHit), as the
   source. Each color reads the exact legacy role, so the shipped ink rules keep matching. */
export const wedgeRailPadDrawsHit = (look: string): boolean => !isWedgeRailPadLook(look);

const ghost = (): CSSProperties => ({
  background: 'transparent', border: `1px solid ${wedgeExactRole('cardBorder')}`, borderRadius: 6, color: wedgeExactRole('labelTer'),
  fontFamily: wedgeExactRole('font'), fontSize: 10.5, fontWeight: 600, letterSpacing: 0.2, padding: '3px 8px', cursor: 'pointer', minHeight: 24,
  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
});
const toned = (on: boolean): CSSProperties => ({
  ...ghost(), color: on ? wedgeRole('red') : wedgeExactRole('labelTer'), borderColor: on ? wedgeRole('red') : wedgeExactRole('separator'),
});

export function wedgeRailPadStyle(look: WedgeRailPadLook): CSSProperties {
  switch (look) {
    case 'railpad-ghost': return ghost();
    case 'railpad-dictate': return {...toned(false), display: 'inline-flex', alignItems: 'center', gap: 4};
    case 'railpad-dictate-on': return {...toned(true), display: 'inline-flex', alignItems: 'center', gap: 4};
    case 'railpad-clear': return toned(false);
    case 'railpad-clear-on': return toned(true);
    case 'railpad-seed':
      return {
        background: wedgeExactRole('accentTint'), color: wedgeExactRole('accent'), border: 0, borderRadius: 999, padding: '5px 10px',
        fontFamily: wedgeExactRole('font'), fontSize: 11, fontWeight: 600, cursor: 'pointer', maxWidth: '100%', overflow: 'hidden',
        textOverflow: 'ellipsis', whiteSpace: 'nowrap', minHeight: 26, WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
      };
    case 'railpad-undo':
      return {
        background: 'none', border: 0, padding: 0, cursor: 'pointer', color: wedgeExactRole('accent'), fontSize: 11, fontWeight: 700,
        fontFamily: wedgeExactRole('font'),
      };
  }
}
